/**
 * Minimal markdown renderer — parse code blocks và inline code
 * Không cần thư viện bên ngoài
 */

export interface RenderedPart {
  type: 'text' | 'code' | 'image';
  content: string;
  language?: string;
  alt?: string;
  url?: string;
}

/** Tách text thường để trích xuất các Markdown image ![alt](url) */
function parseTextWithImages(text: string): RenderedPart[] {
  if (!text) return [];
  const imgRe = /!\[(.*?)\]\((https?:\/\/[^\s\)]+)\)/g;
  const parts: RenderedPart[] = [];
  let lastIdx = 0;
  let match: RegExpExecArray | null;

  while ((match = imgRe.exec(text)) !== null) {
    const before = text.slice(lastIdx, match.index);
    if (before.trim()) {
      parts.push({ type: 'text', content: before });
    }
    parts.push({
      type: 'image',
      alt: match[1] || 'Generated Image',
      url: match[2],
      content: match[0],
    });
    lastIdx = match.index + match[0].length;
  }

  const after = text.slice(lastIdx);
  if (after.trim()) {
    parts.push({ type: 'text', content: after });
  }

  return parts.length ? parts : [{ type: 'text', content: text }];
}

/** Tách message thành các part: text, code block, và image */
export function parseMessage(raw: string): RenderedPart[] {
  if (!raw) return [];

  const parts: RenderedPart[] = [];
  // Match ```lang\n...``` or fallback to end-of-string for unclosed blocks (common during AI streaming)
  const codeBlockRe = /```([^\n`]*)\n?([\s\S]*?)(?:```|$)/g;
  let lastIdx = 0;
  let match: RegExpExecArray | null;

  while ((match = codeBlockRe.exec(raw)) !== null) {
    // Text before block
    const before = raw.slice(lastIdx, match.index);
    if (before) {
      parts.push(...parseTextWithImages(before));
    }

    parts.push({
      type: 'code',
      language: match[1] || 'text',
      content: match[2]?.trim() ?? '',
    });
    lastIdx = match.index + match[0].length;
  }

  const after = raw.slice(lastIdx);
  if (after) {
    parts.push(...parseTextWithImages(after));
  }

  return parts.length ? parts : parseTextWithImages(raw);
}

/** Màu label cho từng ngôn ngữ */
export function langColor(lang: string): string {
  const MAP: Record<string, string> = {
    typescript: '#3178c6', ts: '#3178c6',
    javascript: '#f7df1e', js: '#f7df1e',
    python: '#3572A5', py: '#3572A5',
    rust: '#dea584',
    go: '#00add8',
    sql: '#e38c00',
    bash: '#4eaa25', sh: '#4eaa25',
    json: '#6b7280',
    css: '#563d7c',
    html: '#e34c26',
    jsx: '#61dafb', tsx: '#61dafb',
  };
  return MAP[lang.toLowerCase()] ?? '#6b7280';
}
